import moment from 'moment';
import PropTypes from 'prop-types';

import DataCacheService from '@/shared/services/DataCacheService';
import AuthService from '@/shared/services/AuthService';
import { resourceFilter } from './ResourceAutoComplete';

export const resourceShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  code: PropTypes.string,
  type: PropTypes.oneOf([0, 1, 2, 3, 4, 5]),
});
export const relationShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
});
export const invoiceStreamShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  assignmentId: PropTypes.string,
  assignmentName: PropTypes.string,
  assignmentTemplateId: PropTypes.string,
});
export const priceShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  amount: PropTypes.number,
});
export const registrationShape = PropTypes.shape({
  id: PropTypes.string,
  source: PropTypes.number,
  date: PropTypes.instanceOf(Date),
  type: PropTypes.oneOf([0, 1, 2, 3, 4, 5]),
  invoiceStreamId: PropTypes.string,
  assignmentTemplateId: PropTypes.string,
  relation: relationShape,
  resource: resourceShape,
  plannedTaskId: PropTypes.string,
  durationMinutes: PropTypes.number,
  fromMinutes: PropTypes.number,
  toMinutes: PropTypes.number,
  quantity: PropTypes.number,
  amount: PropTypes.number,
  price: priceShape,
  priceOptions: PropTypes.arrayOf(priceShape),
  discountPercentage: PropTypes.number,
  isBillable: PropTypes.bool,
  inForfait: PropTypes.bool,
  totalAmount: PropTypes.number,
  vatPercentage: PropTypes.number,
  vatType: PropTypes.number,
  referenceYear: PropTypes.number,
});

export const emptyRegistration = {
  source: 0,
  date: moment
    .utc()
    .startOf('day')
    .toDate(),
  type: 0,
  resource: null,

  plannedTaskId: null,
  plannedTaskTemplateId: null,
  plannedTaskTemplateType: null,
  plannedTaskName: null,
  mainPlannedTaskId: null,
  mainTaskTemplateId: null,
  mainPlannedTaskName: null,

  durationType: 0,
  durationMinutes: 0,
  durationDays: 0,
  fromMinutes: 0,
  toMinutes: 0,

  quantity: 0,

  amount: 0, // Unit price
  price: null, // Price object
  priceOptions: null, // Suggested price(s)
  discountPercentage: 0,

  isBillable: true,
  inForfait: null,
  totalAmount: 0,
  vatPercentage: 0.21,
  vatType: 0,

  interactionType: null,
  interactionDescription: null,
  interactionTitle: null,

  referenceYear: null,
};

export const createEmptyRegistration = initialValues => {
  const cache = DataCacheService.getDataCache();
  const tenantDefaults = {};

  if (cache) {
    if (cache.defaultcompany && 'resourceVATRate' in cache.defaultcompany)
      tenantDefaults.vatPercentage = cache.defaultcompany.resourceVATRate;
  }
  if (initialValues && initialValues.date && typeof initialValues.date === 'string')
    initialValues.date = moment.utc(initialValues.date).toDate();

  if (
    initialValues &&
    typeof initialValues.assignmentTemplateId === 'undefined' &&
    initialValues.relation &&
    initialValues.invoiceStreamId
  ) {
    initialValues.assignmentTemplateId = getAssignmentTemplateId(
      initialValues.relation,
      initialValues.invoiceStreamId,
    );
  }

  const referenceYear = sanitizeReferenceYear(initialValues?.referenceYear, initialValues?.date);

  return {
    ...emptyRegistration,
    date: moment
      .utc()
      .startOf('day')
      .toDate(),
    ...tenantDefaults,
    ...(initialValues || {}),
    referenceYear,
  };
};

export const createEmptyTimerRegistration = initialValues =>
  createEmptyRegistration({ fromMinutes: 0, ...(initialValues || {}), source: 5, type: 0 });

export const defaultOptions = {
  showDate: true,
  showReferenceYear: true,
  showRelation: true,
  editableRelation: true,
  ownCompanyRelation: false,
  showAssignments: true,
  showTasks: true,
  resourceTypes: [], // Leave empty for only initial type
  showFromUntil: true,
  showDuration: true,
  showQuantity: true,
  showPrice: true,
  showDiscount: true,
  showVat: true,
  showTotalAmount: true,
  showBillable: true,
  showInForfait: true,
  showInvoiceRemark: true,
  showInternalRemark: true,
  showInteraction: true,
  defaultDuration: 0,
  showOtherUsers: true, //Show readonly username for other users (not for your own registrations)
  crmTypes: [],
};

export const isRegistrationValid = (registration, optionOverrides) => {
  const options = { ...defaultOptions, ...(optionOverrides || {}) };
  if (!registration) return false;
  if (options.showRelation && options.editableRelation) {
    if (!registration.relation || !registration.relation.id) return false;
    if (!registration.invoiceStreamId) return false;
  }
  if (!registration.resource || !registration.resource.id) return false;
  if (
    registration.type === 0 &&
    options.showDuration &&
    (!registration.durationMinutes || registration.durationMinutes <= 0)
  )
    return false;
  if (
    (registration.type === 3 || registration.type === 4) &&
    options.showDuration &&
    (!registration.durationDays || registration.durationDays <= 0) &&
    (!registration.durationMinutes || registration.durationMinutes <= 0)
  )
    return false;

  if (
    (registration.type === 1 || registration.type === 2) &&
    options.showQuantity &&
    (!registration.quantity || registration.quantity <= 0)
  )
    return false;

  return true;
};
export const createEmptyRegistrationFromTask = (task, otherRegistrationValues = {}) => {
  const {
    id: plannedTaskId,
    name: plannedTaskName,
    templateId: plannedTaskTemplateId,
    parentId,
    mainTaskId,
    mainTaskName: mainPlannedTaskName,
    mainTemplateId: mainTaskTemplateId,
    invoiceStreamId,
    assignmentTemplateId,
    relation,
    year,
  } = task;

  const mainPlannedTaskId = parentId || mainTaskId || null;
  return createEmptyRegistration({
    plannedTaskId,
    plannedTaskName,
    plannedTaskTemplateId,
    mainPlannedTaskId,
    mainPlannedTaskName,
    mainTaskTemplateId,
    invoiceStreamId,
    assignmentTemplateId,
    relation,
    referenceYear: year,
    ...otherRegistrationValues,
  });
};

export const getRegistrationModalPropsForTask = (task, orgRegistration) => {
  const { parentId, mainTaskId, resourceId, defaultDuration } = task;
  const mainPlannedTaskId = parentId || mainTaskId || null;
  const registration =
    orgRegistration && orgRegistration.id
      ? createEmptyRegistration(orgRegistration)
      : createEmptyRegistrationFromTask(task, orgRegistration);

  return {
    registration,
    mainTaskId: mainPlannedTaskId,
    defaultResourceId: resourceId,
    registrationOptions: {
      editableRelation: false,
      defaultDuration,
      resourceTypes: [0, 1], // Allow to switch between work and costs
    },
  };
};

export const resourceMatchesFilters = (resource, crmType, assignmentTemplateId) => {
  return resourceFilter(resource, { crmType, assignmentTemplateId }, false);
};
const getAssignmentTemplateId = (relation, invoiceStreamId) => {
  if (!relation || !invoiceStreamId || !relation.invoiceStreams) return null;
  return relation.invoiceStreams.find(x => x.id === invoiceStreamId)?.assignmentTemplateId;
};
export const getFirstSelectedInvoiceStream = relation => {
  if (!relation) return null;
  const { invoiceStreamId: defaultInvoiceStreamId, invoiceStreams } = relation;
  if (!invoiceStreams) return null;
  if (!defaultInvoiceStreamId) {
    //First active invoice stream
    return invoiceStreams.find(x => x.isActive);
  }

  //Order:
  //Default invoice stream if active
  //First active invoice stream
  //Default invoice stream as fallback
  return (
    invoiceStreams.find(x => x.id === defaultInvoiceStreamId && x.isActive) ??
    invoiceStreams.find(x => x.isActive) ??
    invoiceStreams.find(x => x.id === defaultInvoiceStreamId)
  );
};
export const getFirstSelectedInvoiceStreamId = relation =>
  getFirstSelectedInvoiceStream(relation)?.id ?? relation?.invoiceStreamId;
export const sanitizeReferenceYear = (value, registrationDate, isRequired = true) => {
  let year = parseInt(value) || null;
  if (!year || year < 2000 || year > 2200) {
    year = null;
  }
  if (!year && isRequired) {
    if (registrationDate) {
      year = moment.utc(registrationDate).year();
    } else {
      year = moment
        .utc()
        .startOf('day')
        .year();
    }
  }
  return year;
};

export const absenceRegistrationOptions = {
  showDate: true,
  showReferenceYear: false,
  showRelation: true,
  editableRelation: true,
  showAssignments: true,
  showTasks: false,
  showFromUntil: true,
  showDuration: true,
  showQuantity: false,
  showPrice: false,
  showDiscount: false,
  showVat: false,
  showTotalAmount: false,
  showBillable: false,
  showInForfait: false,
  showInvoiceRemark: false,
  showInternalRemark: true,
  showInteraction: false,
  showTraining: false,
};

export const trainingRegistrationOptions = {
  showDate: true,
  showReferenceYear: true,
  showRelation: true,
  editableRelation: true,
  showAssignments: true,
  showTasks: false,
  showFromUntil: true,
  showDuration: true,
  showQuantity: false,
  showPrice: false,
  showDiscount: false,
  showVat: false,
  showTotalAmount: false,
  showBillable: false,
  showInForfait: false,
  showInvoiceRemark: false,
  showInternalRemark: true,
  showInteraction: false,
  showTraining: true,
  trainingReadOnly: false,
};

export const getDefaultRegistrationOptionsForType = resourceType => {
  switch (resourceType) {
    case 3:
    case 4:
      return absenceRegistrationOptions;
    case 5:
      return trainingRegistrationOptions;
    default:
      return {};
  }
};

const maxMinutes = 1439; // 23:59

export const actions = {
  dateChanged: 'dateChanged',
  referenceYearChanged: 'referenceYearChanged',
  fromMinutesChanged: 'fromMinutesChanged',
  toMinutesChanged: 'toMinutesChanged',
  durationTypeChanged: 'durationTypeChanged',
  durationMinutesChanged: 'durationMinutesChanged',
  durationDaysChanged: 'durationDaysChanged',
  isAfternoonChanged: 'isAfternoonChanged',
  addDuration: 'addDuration',
  roundDuration: 'roundDuration',
  unitPriceChanged: 'unitPriceChanged',
  priceChanged: 'priceChanged',
  priceInfoChanged: 'priceInfoChanged',
  setPriceOptions: 'setPriceOptions',
  discountChanged: 'discountChanged',
  vatPercentageChanged: 'vatPercentageChanged',
  calculateTotalAmount: 'calculateTotalAmount',
  totalAmountChanged: 'totalAmountChanged',
  isBillableChanged: 'isBillableChanged',
  inForfaitChanged: 'inForfaitChanged',
  remarkChanged: 'remarkChanged',
  internalRemarkChanged: 'internalRemarkChanged',
  resourceChanged: 'resourceChanged',
  resourceTypeChanged: 'resourceTypeChanged',
  interactionTypeChanged: 'interactionTypeChanged',
  interactionTitleChanged: 'interactionTitleChanged',
  interactionDescriptionChanged: 'interactionDescriptionChanged',
  interactionInit: 'interactionInit',
  taskChanged: 'taskChanged',
  relationChanged: 'relationChanged',
  assignmentChanged: 'assignmentChanged',
  trainingChanged: 'trainingChanged',
  trainingCompletedChanged: 'trainingCompletedChanged',
};
export const executeAction = (registration, action, value) => {
  switch (action) {
    case actions.dateChanged: {
      const date = moment.utc(value).toDate();
      return {
        ...registration,
        date,
        referenceYear:
          registration.plannedTaskId && registration.referenceYear
            ? registration.referenceYear
            : moment.utc(value).year(),
      };
    }
    case actions.referenceYearChanged: {
      const referenceYear = sanitizeReferenceYear(value, registration.date);
      return {
        ...registration,
        referenceYear,
      };
    }
    case actions.fromMinutesChanged: {
      const { durationMinutes } = registration;

      const newToMinutes = value + durationMinutes;
      if (newToMinutes > maxMinutes) {
        return {
          ...registration,
          toMinutes: maxMinutes,
          fromMinutes: maxMinutes - durationMinutes,
        };
      }

      return {
        ...registration,
        toMinutes: newToMinutes,
        fromMinutes: value,
      };
    }
    case actions.toMinutesChanged: {
      const { fromMinutes } = registration;

      const newDuration = value - fromMinutes;
      return executeAction(
        {
          ...registration,
          toMinutes: value,
          durationMinutes: newDuration,
        },
        actions.calculateTotalAmount,
      );
    }
    case actions.durationTypeChanged: {
      return executeAction(
        {
          ...registration,
          durationType: value,
          durationDays: value ? 1 / value : 0,
          isAfternoon: false,
        },
        actions.calculateTotalAmount,
      );
    }
    case actions.isAfternoonChanged: {
      return executeAction({ ...registration, isAfternoon: !!value }, actions.calculateTotalAmount);
    }
    case actions.durationDaysChanged: {
      return executeAction(
        {
          ...registration,
          durationDays: value,
        },
        actions.calculateTotalAmount,
      );
    }
    case actions.durationMinutesChanged: {
      const { fromMinutes } = registration;

      const newToMinutes = fromMinutes + value;
      if (newToMinutes > maxMinutes) {
        return executeAction(
          {
            ...registration,
            toMinutes: maxMinutes,
            durationMinutes: maxMinutes - fromMinutes,
          },
          actions.calculateTotalAmount,
        );
      }
      return executeAction(
        {
          ...registration,
          toMinutes: newToMinutes,
          durationMinutes: value,
        },
        actions.calculateTotalAmount,
      );
    }
    case actions.addDuration: {
      const { durationMinutes = 0 } = registration;
      const newDuration = Math.min(durationMinutes + value, maxMinutes);
      return executeAction(registration, actions.durationMinutesChanged, newDuration);
    }
    case actions.roundDuration: {
      const { durationMinutes = 0 } = registration;
      const newDuration = Math.min(Math.round(durationMinutes / value, 10) * value, maxMinutes);
      return executeAction(registration, actions.durationMinutesChanged, newDuration);
    }
    case actions.quantityChanged: {
      const parsedValue = parseInt(value);
      return executeAction(
        { ...registration, quantity: Number.isNaN(parsedValue) ? null : parsedValue },
        actions.calculateTotalAmount,
      );
    }
    case actions.unitPriceChanged: {
      const parsedValue = parseFloat(value);
      const newPrice =
        !registration.price || registration.price.amount === parsedValue
          ? registration.price
          : null;
      return executeAction(
        {
          ...registration,
          amount: Number.isNaN(parsedValue) ? null : parsedValue,
          price: newPrice,
        },
        actions.calculateTotalAmount,
      );
    }
    case actions.priceChanged: {
      const { amount = 0 } = value || {};
      return executeAction(
        {
          ...registration,
          amount,
          price: value,
        },
        actions.calculateTotalAmount,
      );
    }
    case actions.discountChanged: {
      let parsedValue = parseFloat(value);
      if (Number.isNaN(parsedValue)) {
        parsedValue = null;
      } else {
        if (parsedValue < 0) parsedValue = Math.abs(parsedValue);
        if (parsedValue > 1) parsedValue = 1; // Max 100%
      }

      return executeAction(
        {
          ...registration,
          discountPercentage: parsedValue,
        },
        actions.calculateTotalAmount,
      );
    }
    case actions.vatPercentageChanged: {
      const { vatRate = null, vatType = null } = value || {};
      return {
        ...registration,
        vatPercentage: vatRate,
        vatType,
      };
    }
    case actions.totalAmountChanged: {
      const { type, quantity, durationMinutes, amount, isFixedPrice } = registration;
      if (type === 3 || type === 4) return registration;
      const q = isFixedPrice ? 1 : type === 0 ? (durationMinutes || 0) / 60 : quantity || 0;
      const totalWithoutDiscount = (q * (amount || 0)).round(2);
      let newTotal = (Math.abs(value) || 0).round(2);
      let discountPercentage = 0;

      if (newTotal > totalWithoutDiscount) {
        newTotal = totalWithoutDiscount;
      } else if (totalWithoutDiscount !== 0) {
        discountPercentage = ((totalWithoutDiscount - newTotal) / totalWithoutDiscount).round(2);
      }
      return {
        ...registration,
        totalAmount: newTotal,
        discountPercentage,
      };
    }
    case actions.calculateTotalAmount: {
      const {
        type,
        quantity,
        durationMinutes,
        amount,
        discountPercentage,
        isFixedPrice,
      } = registration;
      if (type === 3 || type === 4) return registration;

      const q = isFixedPrice ? 1 : type === 0 ? (durationMinutes || 0) / 60 : quantity || 0;

      return {
        ...registration,
        totalAmount: (q * (amount || 0) * (1 - (discountPercentage || 0))).round(2),
      };
    }
    case actions.isBillableChanged: {
      return { ...registration, isBillable: !!value };
    }
    case actions.inForfaitChanged: {
      return { ...registration, inForfait: !!value };
    }
    case actions.remarkChanged: {
      return { ...registration, remark: value || null };
    }
    case actions.internalRemarkChanged: {
      return { ...registration, internalRemark: value || null };
    }
    case actions.resourceTypeChanged: {
      const type = parseInt(value);
      return { ...registration, type, resource: null, price: null, inForfait: null };
    }
    case actions.resourceChanged: {
      const {
        resource,
        inForfait,
        priceOptions = null,
        absenceBalance = null,
        durationType = null,
        price,
      } = value;
      let resourceProps = {};
      if (resource !== null) {
        const { isBillable, vatPercentage, vatType, isFixedPrice } = resource;
        resourceProps = { isBillable, vatPercentage, vatType, isFixedPrice };
      }
      let { quantity } = registration;
      if (registration.type === 1 && quantity === 0) quantity = 1;

      let newValue = {
        ...registration,
        quantity,
        resource,
        inForfait,
        priceOptions,
        price: null,
        absenceBalance,

        ...resourceProps,
      };
      if (typeof price !== 'undefined')
        newValue = executeAction(newValue, actions.priceChanged, price);

      if (durationType !== null) {
        newValue = executeAction(newValue, actions.durationTypeChanged, durationType);
      }

      return newValue;
    }
    case actions.priceInfoChanged: {
      const { inForfait, priceOptions = null, price } = value;
      const newValue = {
        ...registration,
        inForfait,
        priceOptions,
        price: null,
      };
      if (typeof price === 'undefined') return newValue;
      return executeAction(newValue, actions.priceChanged, price);
    }
    case actions.setPriceOptions: {
      return {
        ...registration,
        priceOptions: value,
      };
    }
    case actions.interactionTypeChanged: {
      const type = parseInt(value, 10);
      return { ...registration, interactionType: Number.isNaN(type) ? null : type };
    }
    case actions.interactionTitleChanged: {
      let { interactionType } = registration;
      if (!value && !registration.interactionDescription) interactionType = null;
      if (value && (typeof interactionType === 'undefined' || interactionType === null))
        interactionType = 0;

      return { ...registration, interactionTitle: value || null, interactionType };
    }
    case actions.interactionDescriptionChanged: {
      let { interactionType } = registration;
      if (!value && !registration.interactionTitle) interactionType = null;
      if (value && (typeof interactionType === 'undefined' || interactionType === null))
        interactionType = 0;
      return { ...registration, interactionDescription: value || null, interactionType };
    }
    case actions.interactionInit: {
      const { interactionType = null, title = null, description = null, id = null } = value || {};
      return {
        ...registration,
        interactionType,
        interactionTitle: title,
        interactionDescription: description,
        interactionId: id,
        orgInteraction: value,
      };
    }
    case actions.taskChanged: {
      const {
        plannedTaskId = null,
        plannedTaskName = null,
        plannedTaskTemplateId = null,
        plannedTaskTemplateType = null,
        mainPlannedTaskId = null,
        mainPlannedTaskName = null,
        mainTaskTemplateId = null,
        updateResource = null,
        year = null,
      } = value && value.id ? value : {};

      const referenceYear = sanitizeReferenceYear(
        year ?? (registration.plannedTaskId ? null : registration.referenceYear), //Only reset reference year if linked to task
        registration.date,
      );

      const newValue = {
        ...registration,
        plannedTaskId,
        plannedTaskName,
        plannedTaskTemplateId,
        plannedTaskTemplateType,
        mainPlannedTaskId,
        mainPlannedTaskName,
        mainTaskTemplateId,
        referenceYear,
      };
      if (updateResource !== null) {
        // Select resource from task
        return executeAction(newValue, actions.resourceChanged, updateResource);
      }
      return newValue;
    }
    case actions.relationChanged: {
      const invoiceStreamId = getFirstSelectedInvoiceStreamId(value);
      const result = executeAction(
        {
          ...registration,
          relation: value || null,
          invoiceStreamId,
          assignmentTemplateId: getAssignmentTemplateId(value, invoiceStreamId),
        },
        actions.taskChanged,
        null,
      );
      if (
        result.relation &&
        result.resource &&
        !resourceMatchesFilters(result.resource, result.relation?.crm, result.assignmentTemplateId)
      )
        return executeAction(result, actions.resourceChanged, {
          resource: null,
          inForfait: null,
          priceOptions: null,
          price: null,
        });
      return result;
    }
    case actions.assignmentChanged: {
      const result = executeAction(
        {
          ...registration,
          invoiceStreamId: value || null,
          assignmentTemplateId: getAssignmentTemplateId(registration.relation, value || null),
        },
        actions.taskChanged,
        null,
      );
      if (
        result.relation &&
        result.resource &&
        !resourceMatchesFilters(result.resource, result.relation?.crm, result.assignmentTemplateId)
      )
        return executeAction(result, actions.resourceChanged, {
          resource: null,
          inForfait: null,
          priceOptions: null,
          price: null,
        });
      return result;
    }
    case actions.trainingChanged: {
      const newValue = {
        ...registration,
        trainingId: value?.trainingId || null,
        trainingCompleted: value?.trainingEmployeeStatus === 2 ?? false,
      };
      if (value?.updateResource !== null) {
        // Select resource from task
        return executeAction(newValue, actions.resourceChanged, value.updateResource);
      }

      return newValue;
    }
    case actions.trainingCompletedChanged: {
      return {
        ...registration,
        trainingCompleted: value,
      };
    }
    default:
      return registration;
  }
};

export const canCreateRegistrationForUser = (userId = null) =>
  userId == null || AuthService.getUserId() === userId
    ? AuthService.hasPermission({ functionality: 'RegistrationOwn', privilege: 'Create' })
    : AuthService.hasPermission({ functionality: 'RegistrationOthers', privilege: 'Create' });

export const getCurrentTime = () => {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
};
